import React, { useState, useEffect } from 'react';
import s from './ContactModal.module.css';
import { PrivacyPolicy } from '../PrivacyPolicy/PrivacyPolicy';
import { ConsentModal } from '../ConsentModal/ConsentModal';
import { requestsApi } from '../../api/requests';
import { settingsApi } from '../../api/settings';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showConsent, setShowConsent] = useState(false);
    const [privacyContent, setPrivacyContent] = useState('');
    const [consentContent, setConsentContent] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        privacyAccepted: false,
        consentAccepted: false,
    });
    const [sending, setSending] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Загружаем настройки при открытии модалки
    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen]);

    const loadSettings = async () => {
        try {
            const data = await settingsApi.getAllPublic();
            const settingsMap: any = {};
            data.forEach(item => {
                settingsMap[item.key] = item.value;
            });

            setPrivacyContent(settingsMap.privacy_policy_text || '');
            setConsentContent(settingsMap.consent_data_text || '');
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
        }
    };

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!formData.name.trim()) {
            setSubmitError('Пожалуйста, введите ваше имя');
            return;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            setSubmitError('Пожалуйста, введите корректный email');
            return;
        }
        if (!formData.message.trim()) {
            setSubmitError('Пожалуйста, введите сообщение');
            return;
        }
        if (!formData.privacyAccepted) {
            setSubmitError('Пожалуйста, примите политику конфиденциальности');
            return;
        }
        if (!formData.consentAccepted) {
            setSubmitError('Пожалуйста, дайте согласие на обработку персональных данных');
            return;
        }

        try {
            setSending(true);
            await requestsApi.create({
                name: formData.name.trim(),
                email: formData.email.trim(),
                message: formData.message.trim(),
                phone: '',
            });

            setSubmitSuccess(true);
            setFormData({
                name: '',
                email: '',
                message: '',
                privacyAccepted: false,
                consentAccepted: false,
            });

            setTimeout(() => {
                setSubmitSuccess(false);
                onClose();
            }, 2000);
        } catch (error) {
            console.error('Ошибка отправки:', error);
            setSubmitError('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
        } finally {
            setSending(false);
        }
    };

    const renderContent = (content: string) => {
        if (!content) return <p>Нет содержимого</p>;
        try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) {
                return parsed.map((block, index) => {
                    if (block.type === 'paragraph') {
                        const htmlContent = block.content || '';
                        const parts = htmlContent.split('<br />');
                        if (parts.length > 1) {
                            return (
                                <p key={index}>
                                    {parts.map((part: string, i: number) => (
                                        <React.Fragment key={i}>
                                            <span dangerouslySetInnerHTML={{ __html: part }} />
                                            {i < parts.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            );
                        }
                        return <p key={index} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
                    }
                    return null;
                });
            }
            const htmlContent = content;
            const parts = htmlContent.split('<br />');
            if (parts.length > 1) {
                return (
                    <p>
                        {parts.map((part: string, i: number) => (
                            <React.Fragment key={i}>
                                <span dangerouslySetInnerHTML={{ __html: part }} />
                                {i < parts.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                );
            }
            return <p dangerouslySetInnerHTML={{ __html: htmlContent }} />;
        } catch {
            const text = content;
            const lines = text.split('\n');
            if (lines.length > 1) {
                return (
                    <div>
                        {lines.map((line: string, i: number) => (
                            <p key={i} style={{ margin: '4px 0' }}>
                                {line || '\u00A0'}
                            </p>
                        ))}
                    </div>
                );
            }
            return <p>{text}</p>;
        }
    };

    return (
        <>
            <div className={s.overlay} onClick={onClose}>
                <div className={s.modal} onClick={(e) => e.stopPropagation()}>
                    <button className={s.closeButton} onClick={onClose}>×</button>
                    <h2 className={s.title}>Ваши контакты</h2>

                    {submitSuccess && (
                        <div className={s.successMessage}>
                            ✅ Ваше сообщение отправлено!
                        </div>
                    )}

                    {submitError && (
                        <div className={s.errorMessage}>
                            ⚠️ {submitError}
                        </div>
                    )}

                    <form className={s.form} onSubmit={handleSubmit}>
                        <div className={s.formGroup}>
                            <label className={s.label}>Имя *</label>
                            <input
                                type="text"
                                name="name"
                                className={s.input}
                                placeholder="Ваше имя"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={sending}
                            />
                        </div>
                        <div className={s.formGroup}>
                            <label className={s.label}>Email *</label>
                            <input
                                type="email"
                                name="email"
                                className={s.input}
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={sending}
                            />
                        </div>
                        <div className={s.formGroup}>
                            <label className={s.label}>Сообщение *</label>
                            <textarea
                                name="message"
                                className={s.textarea}
                                placeholder="Напишите ваше сообщение..."
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                disabled={sending}
                            />
                        </div>
                        <div className={s.checkboxGroup}>
                            <label className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    name="privacyAccepted"
                                    className={s.checkbox}
                                    checked={formData.privacyAccepted}
                                    onChange={handleCheckboxChange}
                                    disabled={sending}
                                />
                                Я принимаю{' '}
                                <span
                                    className={s.privacyLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowPrivacy(true);
                                    }}
                                >
                                    политику конфиденциальности
                                </span>
                            </label>
                        </div>
                        <div className={s.checkboxGroup}>
                            <label className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    name="consentAccepted"
                                    className={s.checkbox}
                                    checked={formData.consentAccepted}
                                    onChange={handleCheckboxChange}
                                    disabled={sending}
                                />
                                Я даю{' '}
                                <span
                                    className={s.privacyLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowConsent(true);
                                    }}
                                >
                                    согласие на обработку персональных данных
                                </span>
                            </label>
                        </div>
                        <button
                            type="submit"
                            className={s.submitButton}
                            disabled={sending}
                        >
                            {sending ? 'Отправка...' : 'Отправить'}
                        </button>
                    </form>
                </div>
            </div>
            <PrivacyPolicy
                isOpen={showPrivacy}
                onClose={() => setShowPrivacy(false)}
                content={privacyContent}
                renderContent={renderContent}
            />
            <ConsentModal
                isOpen={showConsent}
                onClose={() => setShowConsent(false)}
                content={consentContent}
                renderContent={renderContent}
            />
        </>
    );
};